import type { MeResponse } from '@tabel/shared/schemas/auth';
import type { AnswerResponse, Question, QuestionsResponse } from '@tabel/shared/schemas/questions';
import type { Timesheet } from '@tabel/shared/schemas/timesheets';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  BookOpen,
  CalendarDays,
  CircleAlert,
  CircleCheck,
  Clock,
  Copy,
  Download,
  Info,
  ListChecks,
  MapPin,
  MessageCircleQuestion,
  MoreHorizontal,
  PenLine,
  Search,
  Send,
  Trash2,
  TriangleAlert,
  Undo2,
  UserPlus,
  UserRound,
  Users,
  Zap,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BulkAnswer } from '../../components/BulkAnswer';
import { CompleteDialog } from '../../components/CompleteDialog';
import { type DayActions, DayCard, type DayNav } from '../../components/DayCard';
import { DeleteDraftDialog } from '../../components/DeleteDraftDialog';
import { useToast } from '../../components/ErrorToast';
import { PeoplePanel, people } from '../../components/PeoplePanel';
import { PersonCard } from '../../components/PersonCard';
import { type ListPreset, type ListQuick, QuestionsList } from '../../components/QuestionsList';
import { RecalcNotice } from '../../components/RecalcNotice';
import { type GridQuestions, TimesheetGrid, cellKey, findCell } from '../../components/TimesheetGrid';
import { useUnsavedGuard } from '../../components/UnsavedGuard';
import {
  Button,
  Callout,
  Card,
  Dropdown,
  EmptyState,
  Field,
  IconButton,
  MenuItem,
  MenuSeparator,
  MetaChip,
  Modal,
  PageHeader,
  Segmented,
  SidePanel,
  type Stat,
  StatStrip,
  StatusBadge,
  inputCls,
  textareaCls,
} from '../../components/ui';
import { useUndo } from '../../components/useUndo';
import { api, downloadExport } from '../../lib/api';
import { useSetCrumbs } from '../../lib/crumbs';
import { ApiError, isTransient } from '../../lib/errors';
import {
  type HoursFormat,
  dayOffLabel,
  hoursFormatOf,
  ruDate,
  ruDateTime,
  ruShortDateTime,
  shownHours,
} from '../../lib/format';
import { rulesHref } from '../../lib/nav';
import {
  compareQuestions,
  dayLabel,
  isAnswered,
  isUnanswered,
  nq,
  plural,
  queueDays,
  toReview,
  typeLabel,
} from '../../lib/questions';
import { RECALC_TEXT, recalcInProgress, recalcNoticeOf, staleEditOf } from '../../lib/recalc';
import { type AppRole, useRole } from '../../lib/role';

const SCREEN = 'Timesheet';

/**
 * Правая панель страницы (contracts/ui-panel.md, «Режимы правой панели»): карточка дня, «Добавить людей», «Вопросы
 * по табелю», ответ сразу нескольким, итог очереди «Все вопросы отвечены», карточка сотрудника.
 */
type PanelMode = 'day' | 'people' | 'list' | 'bulk' | 'done' | 'person';

/** Откуда открыта карточка дня: «Назад» в её шапке ведёт туда. */
type DayFrom = 'list' | 'person' | null;

/**
 * Не «пустое место листа»: элементы управления, ячейки, окна и меню. Нажатие на них панель не закрывает
 * (FR-007: закрывает нажатие на пустое место вне панели, не на ячейке).
 */
const NOT_EMPTY =
  'button, a[href], input, select, textarea, label, summary, details, dialog, [role="button"], [role="dialog"], [role="alertdialog"], [role="menu"], [role="menuitem"], [role="tab"], [role="listbox"], [role="option"], [contenteditable="true"], [data-cell]';

/** Ссылка `?questions=…` (сводка при входе, T064): с каким отбором открыть список вопросов. */
const LINK_PRESET: Readonly<Record<string, ListPreset>> = {
  open: { filter: 'open' },
  new: { filter: 'open', quick: 'new' },
  old: { filter: 'open', quick: 'old' },
  done: { filter: 'done' },
};

/**
 * Формат часов правил объекта для правки: по строкам, которые прислал сервер, — сначала ячейка и её дата
 * (правила могут смениться внутри периода), потом весь табель. Табель без движка строк не присылает — `null`.
 */
function hoursFormatFor(ts: Timesheet, personId: string, date: string): HoursFormat | null {
  function* values() {
    const own = ts.rows.find((r) => r.personId === personId)?.cells.find((c) => c.date === date);
    yield own?.machineValue;
    yield own?.finalValue;
    for (const r of ts.rows) {
      const c = r.cells.find((x) => x.date === date);
      yield c?.machineValue;
      yield c?.finalValue;
    }
    yield ts.days.find((d) => d.date === date)?.totalValue;
    for (const r of ts.rows) {
      for (const c of r.cells) {
        yield c.machineValue;
        yield c.finalValue;
      }
    }
    yield ts.grandTotalValue;
  }
  return hoursFormatOf(values());
}

/** Табель на сервере с функцией вопросов: у строк есть счётчик, у ячеек — метка вопроса (T054). */
function hasQuestions(ts: Timesheet | undefined): boolean {
  return Boolean(
    ts?.rows.some((r) => r.openQuestions !== undefined || r.cells.some((c) => c.question !== undefined)),
  );
}

/** Табель не открылся с `ROLE_FORBIDDEN`: почему — по роли. */
function forbiddenText(role: AppRole): string {
  if (role === 'brigadier') return 'Это табель другой бригады. Открыть его нельзя.';
  if (role === 'hr') {
    return 'В этой роли табель недоступен: кадры видят только завершённые и подписанные табели.';
  }
  return 'В этой роли табель недоступен.';
}

/** Вопросы после записи: заменить изменённые, остальные — как были. */
function merge(
  list: QuestionsResponse | undefined,
  changed: readonly Question[],
): QuestionsResponse | undefined {
  if (!list) return list;
  const byId = new Map(changed.map((q) => [q.id, q]));
  return { ...list, questions: list.questions.map((q) => byId.get(q.id) ?? q) };
}

/** Как часто перечитывать табель, пока он пересчитывается по новым правилам. */
const RECALC_POLL_MS = 5000;

/**
 * Табель: режим бригадира (US1, US2) и кадров (US3), выгрузка (US4). Любая прошедшая и сегодняшняя ячейка открывает
 * карточку дня в правой панели (003, US1); «Добавить людей», «Вопросы по табелю», ответ сразу нескольким — режимы той
 * же панели. Панель закрывают ✕, Esc, повторное нажатие на выбранную ячейку и нажатие на пустое место листа; при
 * несохранённом — «Сохранить …?» (`guard`). Фокус после закрытия — на ячейке.
 * Вопросы бригадиру (003, US2): метки в таблице, полоса показателей, главная кнопка «Ответить на вопросы (N)»,
 * очередь от старой даты к новой, подсказка группы, «Завершить» — сначала сводка; кадры — «Принято», «Уточнить».
 */
export function TimesheetPage({ me }: { me: MeResponse }) {
  const id = Number(useParams().id);
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const { guard } = useUnsavedGuard();
  const key = ['timesheet', id, role];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get(id),
    // Пока табель пересчитывается по новым правилам (004, FR-043), страница спрашивает снова: когда пересчёт
    // закончится, «Подписать» станет доступна, а в ячейках будут новые часы.
    refetchInterval: (query) => (recalcInProgress(query.state.data) ? RECALC_POLL_MS : false),
  });
  const home = role === 'hr' ? '/hr' : '/brigadier';
  useSetCrumbs([
    { label: role === 'hr' ? 'На проверку' : 'Табели', to: home },
    { label: q.data?.brigadeName ?? 'Табель' },
  ]);

  const [panel, setPanel] = useState<PanelMode | null>(null);
  /** День карточки. Остаётся после закрытия панели: на его ячейку возвращается фокус. */
  const [day, setDay] = useState<{ personId: string; date: string } | null>(null);
  /** Откуда открыта карточка дня (список вопросов, карточка сотрудника): в шапке «Назад»; с каким вопросом. */
  const [dayFrom, setDayFrom] = useState<{ back: DayFrom; questionId: number | null }>({
    back: null,
    questionId: null,
  });
  /** Человек карточки сотрудника. */
  const [person, setPerson] = useState<string | null>(null);
  /** Над табелем: поиск по ФИО и № СКУД, «Все люди / С вопросами» (FR-040, FR-024). */
  const [search, setSearch] = useState('');
  const [rowsFilter, setRowsFilter] = useState<'all' | 'q'>('all');
  /** С какой вкладки открыть «Добавить людей»: пустой табель — «Скопировать состав». */
  const [peopleTab, setPeopleTab] = useState<'scud' | 'copy'>('scud');
  /** Открыто «Ответить на вопросы»: после ответа — следующий день; сколько ответов дано за проход. */
  const [queue, setQueue] = useState(false);
  const [answeredInQueue, setAnsweredInQueue] = useState(0);
  /** С каким отбором открыть список; `n` — новый отбор, новый список. */
  const [listPreset, setListPreset] = useState<{ preset: ListPreset; n: number }>({ preset: {}, n: 0 });
  const [bulk, setBulk] = useState<{ ids: number[]; fromList: boolean }>({ ids: [], fromList: false });
  const [completing, setCompleting] = useState(false);
  const [returning, setReturning] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [signing, setSigning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const [params, setParams] = useSearchParams();

  const closePanel = useCallback(() => setPanel(null), []);
  const guardRef = useRef(guard);
  guardRef.current = guard;

  // Нажатие на пустое место листа (вне панели, не на ячейке и не на кнопке) закрывает панель — через guard.
  const panelOpen = panel !== null;
  useEffect(() => {
    if (!panelOpen) return;
    const onClick = (e: MouseEvent) => {
      const page = pageRef.current;
      const sheet = page?.closest('[data-sheet]') ?? page;
      const t = e.target;
      if (!sheet || !(t instanceof Element) || !sheet.contains(t) || t.closest(NOT_EMPTY)) return;
      if (window.getSelection()?.toString()) return; // выделяли текст
      guardRef.current(closePanel);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [panelOpen, closePanel]);

  // Сменилась роль — панель закрыта: действия и права другие.
  // biome-ignore lint/correctness/useExhaustiveDependencies: только при смене роли
  useEffect(() => setPanel(null), [role]);

  // Вопросы по табелю (003): только у табеля с функцией вопросов.
  const loaded = q.data;
  const questionsOn = hasQuestions(loaded);
  const qkey = ['questions', id, role];
  const qq = useQuery({ queryKey: qkey, queryFn: () => api.questions(id), enabled: questionsOn });
  const qdata = qq.data && Array.isArray(qq.data.questions) ? qq.data : undefined;
  const allQs = qdata?.questions ?? [];

  // Ссылка на день (`?person=…&date=…`, «Скопировать ссылку на день»): открыть карточку и убрать параметры.
  const linkPerson = params.get('person');
  const linkDate = params.get('date');
  useEffect(() => {
    if (!loaded || !linkPerson || !linkDate) return;
    const row = loaded.rows.find((r) => r.personId === linkPerson);
    const d = loaded.days.find((x) => x.date === linkDate);
    if (row && d && !d.future) {
      setDay({ personId: linkPerson, date: linkDate });
      setDayFrom({ back: null, questionId: null });
      setQueue(false);
      setPanel('day');
    } else {
      showInfo('Дня по ссылке нет в этом табеле');
    }
    setParams(
      (p) => {
        p.delete('person');
        p.delete('date');
        return p;
      },
      { replace: true },
    );
  }, [loaded, linkPerson, linkDate, setParams, showInfo]);

  // Ссылка со списком вопросов (`?questions=new|old|open|done`, сводка при входе): открыть список и убрать параметр.
  const linkQuestions = params.get('questions');
  useEffect(() => {
    if (!loaded || !linkQuestions) return;
    const preset = LINK_PRESET[linkQuestions];
    if (preset && hasQuestions(loaded)) {
      setListPreset((x) => ({ preset, n: x.n + 1 }));
      setPanel('list');
    }
    setParams(
      (p) => {
        p.delete('questions');
        return p;
      },
      { replace: true },
    );
  }, [loaded, linkQuestions, setParams]);

  // Человека убрали из табеля — его карточка дня и карточка сотрудника закрыты.
  const dayGone =
    (panel === 'day' && loaded !== undefined && !loaded.rows.some((r) => r.personId === day?.personId)) ||
    (panel === 'person' && loaded !== undefined && !loaded.rows.some((r) => r.personId === person));
  useEffect(() => {
    if (dayGone) setPanel(null);
  }, [dayGone]);

  const refresh = (ts?: Timesheet) => {
    if (ts) qc.setQueryData(key, ts);
    else void qc.invalidateQueries({ queryKey: ['timesheet', id] });
    void qc.invalidateQueries({ queryKey: ['calc', id] });
    void qc.invalidateQueries({ queryKey: ['questions', id] });
    void qc.invalidateQueries({ queryKey: ['summary', id] });
    // Серый счётчик вопросов у «Табели» — из списка табелей; сводка о вопросах при входе — тоже.
    void qc.invalidateQueries({ queryKey: ['timesheets'] });
    void qc.invalidateQueries({ queryKey: ['questions', 'summary'] });
    // Удалить, а не перезапросить: после «Завершить» кандидаты не нужны и вернули бы 409.
    qc.removeQueries({ queryKey: ['candidates', id] });
  };
  /**
   * «Отменить» в тосте (FR-038): табель и вопросы из ответа — сразу на экран; табель, вопросы табеля, расчёт дня,
   * сводка перед «Завершить», список табелей (счётчик у «Табели») и сводка при входе — перечитать, как после ответа и
   * после добавления или удаления людей. Отказ отмены — тоже перечитать: данные могли измениться.
   */
  const undo = useUndo({
    screen: SCREEN,
    onDone: (res) => {
      const changed = res.questions ?? [];
      if (changed.length > 0) qc.setQueryData<QuestionsResponse>(qkey, (old) => merge(old, changed));
      if (res.timesheet) qc.setQueryData(key, res.timesheet);
      refresh();
    },
  });
  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: (res) =>
      refresh(res && typeof res === 'object' && 'rows' in res ? (res as Timesheet) : undefined),
    // Сбой связи или сервера — «Повторить» то же действие; отказ по статусу или правам — только причина.
    onError: (e, fn) =>
      showError(e, {
        screen: SCREEN,
        action: 'mutation',
        retry: isTransient(e) ? () => run.mutate(fn) : undefined,
      }),
  });

  if (q.isLoading) return <p className="text-[13px] text-ink-3">Загрузка…</p>;
  if (q.error || !q.data) {
    const e = q.error;
    const msg =
      e instanceof ApiError && e.code === 'ROLE_FORBIDDEN'
        ? forbiddenText(role)
        : e instanceof ApiError
          ? `${e.message}${e.requestId ? ` · код ошибки: ${e.requestId}` : ''}`
          : 'Не удалось загрузить табель';
    return (
      <Card>
        <EmptyState
          icon={CircleAlert}
          title="Табель не открылся"
          description={msg}
          action={
            <Link
              to={home}
              className="inline-flex h-8 items-center rounded-control border border-line px-3 text-[13px] font-medium hover:bg-subtle"
            >
              К списку табелей
            </Link>
          }
        />
      </Card>
    );
  }

  const ts = q.data;
  const brigadierEdits = role === 'brigadier' && (ts.status === 'draft' || ts.status === 'returned');
  const hrEdits = role === 'hr' && ts.status === 'completed';
  // Пересчёт по новым правилам (004, US3): плашка над таблицей и замок подписи.
  const notice = recalcNoticeOf(ts);
  const recalcLock = recalcInProgress(ts);
  const selRow = day ? ts.rows.find((r) => r.personId === day.personId) : undefined;
  const selCell = (day && selRow?.cells.find((c) => c.date === day.date)) || null;
  const selDay = day ? ts.days.find((d) => d.date === day.date) : undefined;
  const format = (personId: string, date: string) => hoursFormatFor(ts, personId, date);
  const anyFormat = ts.rows[0]?.cells[0] ? format(ts.rows[0].personId, ts.rows[0].cells[0].date) : null;

  // Что требует действия в этой роли: у бригадира — вопрос без ответа, у кадров — ответ на проверку.
  const review = role === 'hr';
  const pick = (x: Question) => (review ? toReview(x) : isUnanswered(x));
  const unanswered = allQs.filter(isUnanswered);
  const nameOf = (personId: string) => ts.rows.find((r) => r.personId === personId)?.displayName ?? '';
  const order = (a: { personId: string; date: string }, b: { personId: string; date: string }) =>
    a.date !== b.date
      ? a.date < b.date
        ? -1
        : 1
      : nameOf(a.personId).localeCompare(nameOf(b.personId), 'ru');
  const navDays = queueDays(allQs, pick);
  const latestQuestions = () => qc.getQueryData<QuestionsResponse>(qkey)?.questions ?? allQs;

  /** Открыть карточку дня (из ячейки, списка или очереди). */
  const showDay = (
    personId: string,
    date: string,
    o: { from?: DayFrom; questionId?: number | null; queue?: boolean } = {},
  ) => {
    setDay({ personId, date });
    setDayFrom({ back: o.from ?? null, questionId: o.questionId ?? null });
    setQueue(o.queue ?? false);
    setPanel('day');
  };

  /** Ячейка: другая — открыть её карточку (через guard), та же — закрыть панель. */
  const onCellClick = (personId: string, date: string) => {
    const same = panel === 'day' && day?.personId === personId && day.date === date;
    guard(() => {
      if (same) {
        setPanel(null);
        return;
      }
      showDay(personId, date);
    });
  };
  const openPeople = (tab: 'scud' | 'copy' = 'scud') =>
    guard(() => {
      setPeopleTab(tab);
      setPanel('people');
    });
  /** ФИО в табеле — карточка сотрудника (FR-034). */
  const openPerson = (personId: string) =>
    guard(() => {
      setPerson(personId);
      setPanel('person');
    });
  const openList = (preset: ListPreset) =>
    guard(() => {
      setListPreset((x) => ({ preset, n: x.n + 1 }));
      setPanel('list');
    });

  /** «Ответить на вопросы»: первый день с вопросом без ответа, от старой даты к новой (FR-025). */
  const startQueue = () =>
    guard(() => {
      const days = queueDays(latestQuestions(), pick);
      const first = days[0];
      if (!first) {
        showInfo(review ? 'Ответов на проверку нет' : 'Вопросов без ответа нет');
        return;
      }
      setAnsweredInQueue(0);
      showDay(first.personId, first.date, { queue: true });
    });

  /** ‹ › и «Сохранить и дальше»: соседний день очереди; очередь кончилась — итог «Все вопросы отвечены». */
  const step = (dir: 1 | -1) =>
    guard(() => {
      const list = latestQuestions();
      const days = queueDays(list, pick);
      const cur = day;
      const others = cur ? days.filter((d) => !(d.personId === cur.personId && d.date === cur.date)) : days;
      const after = cur ? others.filter((d) => order(d, cur) * dir > 0) : others;
      const next = dir > 0 ? (after[0] ?? others[0]) : (after[after.length - 1] ?? others[others.length - 1]);
      if (next) {
        showDay(next.personId, next.date, { queue });
        return;
      }
      if (queue && !review && !list.some(isUnanswered)) {
        setPanel('done');
        return;
      }
      if (days.length === 0) showInfo(review ? 'Ответов на проверку нет' : 'Вопросов без ответа нет');
    });

  /** Ответ записан: вопросы — сразу из ответа сервера, табель и счётчики — перечитать. */
  const onAnswered = (res: AnswerResponse) => {
    qc.setQueryData<QuestionsResponse>(qkey, (old) => merge(old, res.questions));
    if (queue) setAnsweredInQueue((n) => n + res.questions.filter(isAnswered).length);
    refresh();
  };
  const onReviewed = (changed: Question) => {
    qc.setQueryData<QuestionsResponse>(qkey, (old) => merge(old, [changed]));
    refresh();
  };
  /** Вопрос изменился или снят (`409`): текущие вопросы из ответа — сразу на экран, остальное — перечитать. */
  const onStale = (current: Question[]) => {
    if (current.length > 0) qc.setQueryData<QuestionsResponse>(qkey, (old) => merge(old, current));
    refresh();
  };

  /**
   * Сразу сохраняемое действие с ячейкой: обновить табель и расчёт; ошибка — тост с «Повторить» (кроме «День
   * изменился»: новое состояние уже на экране).
   */
  const cellNow = async (action: string, fn: () => Promise<unknown>): Promise<unknown> => {
    try {
      const res = await fn();
      refresh();
      return res;
    } catch (e) {
      const stale = e instanceof ApiError && e.code === 'STALE_STATE';
      if (stale) refresh();
      showError(e, {
        screen: SCREEN,
        action,
        lead: 'Не сохранено',
        retry: stale ? undefined : () => void cellNow(action, fn).catch(() => {}),
      });
      throw e;
    }
  };
  const actions: DayActions = {};
  if (day && selCell && (brigadierEdits || hrEdits)) {
    const { personId, date } = day;
    actions.edit = async (h, comment, base) => {
      try {
        const res = await api.edit(id, personId, date, h, comment, base);
        refresh();
        return res;
      } catch (e) {
        // «День изменился»: новое состояние — в карточке, ввод остаётся в форме.
        if (e instanceof ApiError && e.code === 'STALE_STATE') refresh();
        throw e;
      }
    };
    actions.cancelEdit = (base) => cellNow('cancelEdit', () => api.cancelEdit(id, personId, date, base));
    if (brigadierEdits) actions.lunch = (v) => cellNow('lunch', () => api.lunch(id, personId, date, v));
    if (hrEdits) actions.absence = (code) => cellNow('absence', () => api.absence(id, personId, date, code));
    // «Правка устарела» (FR-044): решают кадры в «Завершён».
    if (hrEdits && staleEditOf(selCell))
      actions.editDecision = (decision) =>
        cellNow('editDecision', () => api.editDecision(id, personId, date, decision));
  }

  const copyLink = () => {
    if (!day) return;
    const query = new URLSearchParams({ person: day.personId, date: day.date });
    const url = `${window.location.origin}/timesheets/${id}?${query}`;
    const manual = () => showInfo(`Скопируйте ссылку: ${url}`, { duration: 8000 });
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => showInfo('Ссылка на день скопирована', { duration: 2000 }), manual);
    } else manual();
  };

  /** Убрать человека из табеля (карточка сотрудника, FR-035): тост с числом снятых вопросов и «Отменить». */
  const removePerson = async (personId: string): Promise<void> => {
    const name = nameOf(personId);
    try {
      const res = await api.removeRow(id, personId);
      const { undoActionId: _u, removedQuestions, ...next } = res;
      refresh(next);
      setPanel(null);
      showInfo(
        `Убрано из табеля: ${name}${removedQuestions ? `. Снято вопросов: ${removedQuestions}` : ''}`,
        { action: undo(res) },
      );
    } catch (e) {
      showError(e, {
        screen: SCREEN,
        action: 'removeRow',
        lead: 'Не убрано',
        retry: () => void removePerson(personId).catch(() => {}),
      });
      throw e;
    }
  };
  /** Добавить отмеченных людей: тост с «Отменить»; ошибка — «Повторить», выбор остаётся в панели. */
  const addPeople = async (ids: string[]): Promise<void> => {
    try {
      const res = await api.addRows(id, ids);
      const { undoActionId: _u, ...next } = res;
      refresh(next);
      setPanel(null);
      showInfo(`Добавлено: ${people(ids.length)}`, { action: undo(res) });
    } catch (e) {
      showError(e, {
        screen: SCREEN,
        action: 'addRows',
        lead: 'Не добавлено',
        note: 'Выбор остался в панели.',
        retry: () => void addPeople(ids).catch(() => {}),
      });
      throw e;
    }
  };
  /** Скопировать состав другого табеля: тост с итогом и «Отменить»; ошибка — «Повторить». */
  const copyPeople = async (sourceId: number): Promise<void> => {
    try {
      const res = await api.copyRows(id, sourceId);
      refresh();
      setPanel(null);
      showInfo(
        `Скопировано: ${res.added}${res.skipped ? `; пропущено ${res.skipped} — нет проходов за период` : ''}`,
        { action: undo(res) },
      );
    } catch (e) {
      showError(e, {
        screen: SCREEN,
        action: 'copyRows',
        lead: 'Состав не скопирован',
        retry: () => void copyPeople(sourceId).catch(() => {}),
      });
      throw e;
    }
  };
  /** «Ответить на вопросы (N)» в карточке сотрудника: первый его день с вопросом без ответа. */
  const answerPerson = (personId: string) =>
    guard(() => {
      const first = latestQuestions()
        .filter((x) => x.personId === personId && isUnanswered(x))
        .sort(compareQuestions)[0];
      if (!first) {
        showInfo('Вопросов без ответа нет');
        return;
      }
      setAnsweredInQueue(0);
      showDay(first.personId, first.date, { queue: true, questionId: first.id });
    });

  const navIdx = day ? navDays.findIndex((d) => d.personId === day.personId && d.date === day.date) : -1;
  const nav: DayNav | null =
    questionsOn && (brigadierEdits || hrEdits)
      ? {
          index: navIdx >= 0 ? navIdx : null,
          total: navDays.length,
          prev: () => step(-1),
          next: () => step(1),
          queue,
          hasOther: navDays.some((d) => !(d.personId === day?.personId && d.date === day?.date)),
          review,
        }
      : null;

  // Числа вопросов у дат и людей (FR-022): у бригадира — без ответа, у кадров — ответы на проверку.
  let grid: GridQuestions | null = null;
  if (questionsOn) {
    const byDate = new Map<string, number>();
    const byPerson = new Map<string, number>();
    if (qdata) {
      for (const x of allQs.filter(pick)) {
        byDate.set(x.date, (byDate.get(x.date) ?? 0) + 1);
        byPerson.set(x.personId, (byPerson.get(x.personId) ?? 0) + 1);
      }
    } else if (!review) {
      // Список ещё не пришёл: числа из табеля (метки ячеек и счётчики строк).
      for (const r of ts.rows) {
        if (r.openQuestions) byPerson.set(r.personId, r.openQuestions);
        for (const c of r.cells) if (c.question === 'open') byDate.set(c.date, (byDate.get(c.date) ?? 0) + 1);
      }
    }
    grid = {
      byDate,
      byPerson,
      review,
      onDate: (date) => openList({ filter: review ? 'done' : 'open', quick: review ? 'review' : null, date }),
      onPerson: (personId) =>
        openList({ filter: review ? 'done' : 'open', quick: review ? 'review' : null, personId }),
    };
  }

  // Над табелем (FR-040, FR-024): поиск по ФИО и № СКУД, «Все люди / С вопросами». Итоги — по всему табелю.
  const needle = search.trim().toLocaleLowerCase('ru');
  const hasQ = (personId: string) => (grid?.byPerson.get(personId) ?? 0) > 0;
  const withQ = ts.rows.filter((r) => hasQ(r.personId)).length;
  const filterQ = Boolean(grid) && rowsFilter === 'q';
  const visible =
    needle || filterQ
      ? new Set(
          ts.rows
            .filter(
              (r) =>
                (!needle ||
                  r.displayName.toLocaleLowerCase('ru').includes(needle) ||
                  r.personId.toLocaleLowerCase('ru').includes(needle)) &&
                (!filterQ || hasQ(r.personId)),
            )
            .map((r) => r.personId),
        )
      : null;

  // Данные СКУД (FR-039): время последнего прохода и тишина источника дольше порога объекта.
  const lastPass = ts.lastPassAt ?? me.lastPassAt;
  const stale = ts.dataStale ?? me.dataStale ?? false;
  const lastPassText = lastPass ? ruShortDateTime(lastPass, me.object.tzOffset) : null;

  // Подсказка группы (FR-021): за одну дату вопросов одного типа не меньше порога объекта.
  let group: { date: string; type: string; ids: number[] } | null = null;
  if (brigadierEdits && qdata) {
    const groups = new Map<string, Question[]>();
    for (const x of unanswered)
      groups.set(`${x.date}|${x.type}`, [...(groups.get(`${x.date}|${x.type}`) ?? []), x]);
    const threshold = qdata.params.groupThreshold;
    const hit = [...groups.values()]
      .filter((g) => g.length >= threshold)
      .sort((a, b) => ((a[0]?.date ?? '') < (b[0]?.date ?? '') ? -1 : 1))[0];
    if (hit?.[0]) {
      const ids = [...hit].sort((a, b) => order(a, b)).map((x) => x.id);
      group = { date: hit[0].date, type: hit[0].type, ids };
    }
  }

  const clarify = allQs.filter((x) => x.state === 'clarify');
  const openReturn = () =>
    guard(() => {
      if (!returnComment.trim() && clarify.length > 0) {
        setReturnComment(
          `Уточните ответы:\n${clarify
            .map((x) => `— ${x.displayName}, ${ruDate(x.date).slice(0, 5)}: ${x.review?.comment ?? ''}`)
            .join('\n')}`,
        );
      }
      setReturning(true);
    });

  const answeredCount = allQs.filter(isAnswered).length;
  const stats: Stat[] | null =
    !questionsOn || !qdata
      ? null
      : review
        ? [
            {
              key: 'review',
              icon: ListChecks,
              label: 'Ответы на проверку',
              value: allQs.filter(toReview).length,
              of: answeredCount,
              note: 'откроются в панели справа',
              onClick: () => openList({ filter: 'done', quick: 'review' }),
            },
            {
              key: 'accepted',
              icon: CircleCheck,
              label: 'Принято',
              value: allQs.filter((x) => x.state === 'accepted').length,
              note: 'ответы проверены',
              noteTone: 'ok',
              onClick: () => openList({ filter: 'done' }),
            },
            {
              key: 'clarify',
              icon: Undo2,
              label: 'Уточнить',
              value: clarify.length,
              note: 'уйдут бригадиру',
              noteTone: clarify.length ? 'warn' : undefined,
              onClick: () => openList({ filter: 'open' }),
            },
            {
              key: 'open',
              icon: MessageCircleQuestion,
              label: 'Без ответа',
              value: allQs.filter((x) => x.state === 'open' || x.state === 'later').length,
              note: 'бригадир не ответил',
              onClick: () => openList({ filter: 'open' }),
            },
          ]
        : [
            {
              key: 'open',
              icon: MessageCircleQuestion,
              label: 'Без ответа',
              value: unanswered.length,
              of: allQs.length,
              note: unanswered.length ? 'список откроется справа' : 'все вопросы отвечены',
              noteTone: unanswered.length ? undefined : 'ok',
              onClick: () => openList({ filter: 'open' }),
            },
            {
              key: 'old',
              icon: Clock,
              label: 'Давно без ответа',
              value: allQs.filter((x) => x.old && isUnanswered(x)).length,
              note: `больше ${qdata.params.oldAfterDays} ${plural(qdata.params.oldAfterDays, ['день', 'дня', 'дней'])}`,
              noteTone: allQs.some((x) => x.old && isUnanswered(x)) ? 'bad' : undefined,
              onClick: () => openList({ filter: 'open', quick: 'old' }),
            },
            {
              key: 'new',
              icon: Bell,
              label: 'Новые',
              value: allQs.filter((x) => x.isNew).length,
              note: 'появились за вчера',
              onClick: () => openList({ filter: 'open', quick: 'new' satisfies ListQuick }),
            },
            {
              key: 'answered',
              icon: CircleCheck,
              label: 'Отвечено',
              value: answeredCount,
              progress: allQs.length ? answeredCount / allQs.length : 0,
              note: `${allQs.length ? Math.round((answeredCount / allQs.length) * 100) : 0}%`,
              onClick: () => openList({ filter: 'done' }),
            },
          ];

  const bulkQs = bulk.ids.map((x) => allQs.find((y) => y.id === x)).filter((x): x is Question => Boolean(x));

  return (
    <div ref={pageRef} className="space-y-6">
      <PageHeader
        title={
          <>
            {ts.brigadeName}
            <StatusBadge status={ts.status} />
          </>
        }
        titleAside={
          // Меню «…» у статуса (FR-036): редкие действия с табелем; опасное — последним, за разделителем.
          <Dropdown
            width="w-[260px]"
            trigger={({ open, toggle }) => (
              <IconButton
                icon={MoreHorizontal}
                label="Действия с табелем"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={toggle}
              />
            )}
          >
            {(close) => (
              <>
                <MenuItem
                  icon={Download}
                  onClick={() => {
                    close();
                    downloadExport(id).catch((e) => showError(e, { screen: SCREEN, action: 'export' }));
                  }}
                >
                  Скачать Excel
                </MenuItem>
                <MenuItem
                  icon={BookOpen}
                  onClick={() => {
                    close();
                    // «Правила расчёта» на дату начала периода; период — выбор версии, если их две (004, FR-015).
                    guard(() =>
                      navigate(
                        rulesHref({
                          date: ts.periodStart,
                          start: ts.periodStart,
                          end: ts.periodEnd,
                          from: `/timesheets/${id}`,
                        }),
                      ),
                    );
                  }}
                >
                  Как считаются часы
                </MenuItem>
                {role === 'brigadier' && ts.status === 'draft' && (
                  <>
                    <MenuSeparator />
                    <MenuItem
                      icon={Trash2}
                      danger
                      onClick={() => {
                        close();
                        guard(() => setDeleting(true));
                      }}
                    >
                      Удалить черновик
                    </MenuItem>
                  </>
                )}
              </>
            )}
          </Dropdown>
        }
        meta={
          <>
            <MetaChip icon={MapPin} label="Объект">
              {me.object.name}
            </MetaChip>
            <MetaChip icon={CalendarDays} label="Период">
              {ruDate(ts.periodStart)} — {ruDate(ts.periodEnd)}
            </MetaChip>
            <MetaChip icon={Users} label="Людей">
              {ts.rows.length}
            </MetaChip>
            <MetaChip icon={Clock} label="Часов">
              {shownHours(ts.grandTotalValue, ts.grandTotal) || '0,0'}
            </MetaChip>
            <MetaChip
              icon={stale ? TriangleAlert : CircleCheck}
              label="Данные СКУД"
              tone={stale ? 'warn' : undefined}
              title={stale ? 'Новых проходов давно нет' : 'Время последнего полученного прохода'}
            >
              {lastPassText ?? 'проходов нет'}
            </MetaChip>
            <MetaChip icon={UserRound} label="Заполняет">
              {me.user.fullName}, {me.user.position}
            </MetaChip>
          </>
        }
        actions={
          <>
            {brigadierEdits && (
              <Button icon={UserPlus} onClick={() => openPeople()}>
                Добавить людей
              </Button>
            )}
            {brigadierEdits && (
              <Button
                variant={unanswered.length > 0 ? 'secondary' : 'primary'}
                icon={Send}
                disabled={run.isPending}
                onClick={() => guard(() => setCompleting(true))}
              >
                Завершить
              </Button>
            )}
            {brigadierEdits && unanswered.length > 0 && (
              <Button variant="primary" icon={MessageCircleQuestion} onClick={startQueue}>
                Ответить на вопросы
                <span className="rounded-full bg-white/20 px-1.5 text-[12px] font-semibold tabular-nums">
                  {unanswered.length}
                </span>
              </Button>
            )}
            {hrEdits && (
              <>
                {recalcLock && (
                  <p
                    id="recalc-lock"
                    className="order-last basis-full text-right text-[12.5px] text-ink-2 max-tablet:text-left"
                  >
                    {RECALC_TEXT.inProgress}
                  </p>
                )}
                <Button icon={Undo2} onClick={openReturn}>
                  Вернуть бригадиру
                </Button>
                <Button
                  variant="primary"
                  icon={PenLine}
                  disabled={recalcLock}
                  aria-describedby={recalcLock ? 'recalc-lock' : undefined}
                  onClick={() => guard(() => setSigning(true))}
                >
                  Подписать
                </Button>
              </>
            )}
          </>
        }
      />

      {stale && (
        <div data-stale-note>
          <Callout tone="warn" icon={TriangleAlert}>
            <b className="font-semibold">
              {lastPassText ? `С ${lastPassText} новых проходов нет.` : 'Новых проходов нет.'}
            </b>{' '}
            Часы за сегодня неполные. Сообщите администратору объекта.
          </Callout>
        </div>
      )}

      {questionsOn && role === 'brigadier' && ts.status === 'returned' ? (
        <div
          data-returned
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control border border-warn/25 bg-warn-soft px-3.5 py-2.5 text-[13px] text-ink"
        >
          <Undo2 size={16} strokeWidth={1.75} className="shrink-0 text-warn" aria-hidden />
          <span className="min-w-0 flex-1 basis-[220px]">
            <b className="font-semibold">Кадры вернули табель.</b>{' '}
            {/* Комментарий при возврате — когда сервер его прислал. */}
            {ts.returnComment ? `«${ts.returnComment}» ` : ''}
            {clarify.length > 0
              ? `Уточнить: ${nq(clarify.length)}. Комментарии к вопросам — в карточке дня.`
              : 'Исправьте часы и нажмите «Завершить».'}
          </span>
          {unanswered.length > 0 && (
            <Button size="sm" onClick={startQueue}>
              Ответить
            </Button>
          )}
        </div>
      ) : (
        <StatusNote ts={ts} role={role} tzOffset={me.object.tzOffset} />
      )}

      {notice && (
        <RecalcNotice
          timesheetId={id}
          notice={notice}
          nameOf={nameOf}
          onOpenDay={(personId, date) => guard(() => showDay(personId, date))}
          onDismissed={() => void qc.invalidateQueries({ queryKey: ['timesheet', id] })}
        />
      )}

      {stats && <StatStrip label="Вопросы по табелю" items={stats} />}

      {group && (
        <div
          data-group-hint
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control border border-accent/20 bg-accent-soft px-3.5 py-2.5 text-[13px] text-ink"
        >
          <Zap size={16} strokeWidth={1.75} className="shrink-0 text-accent" aria-hidden />
          <span className="min-w-0 flex-1 basis-[220px]">
            <b className="font-semibold">{`${dayLabel(group.date)}: ${typeLabel(group.type).toLowerCase()} у ${group.ids.length} человек.`}</b>{' '}
            Так бывает, когда не работает терминал. Ответьте один раз — часы посчитаются по каждому.
          </span>
          <Button
            size="sm"
            onClick={() =>
              guard(() => {
                setBulk({ ids: group.ids, fromList: false });
                setPanel('bulk');
              })
            }
          >
            Ответить всем сразу
          </Button>
        </div>
      )}

      {ts.rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Users}
            title="В табеле пока никого нет"
            description="Добавьте людей, которых СКУД видел на объекте, или скопируйте состав прошлого табеля."
            action={
              brigadierEdits && (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button icon={Copy} onClick={() => openPeople('copy')}>
                    Скопировать состав
                  </Button>
                  <Button variant="primary" icon={UserPlus} onClick={() => openPeople()}>
                    Добавить людей
                  </Button>
                </div>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          <div data-grid-bar className="flex flex-wrap items-center gap-2">
            {grid && (
              <Segmented<'all' | 'q'>
                label="Строки"
                value={rowsFilter}
                onChange={setRowsFilter}
                options={[
                  { value: 'all', label: 'Все люди', count: ts.rows.length },
                  { value: 'q', label: review ? 'С ответами' : 'С вопросами', count: withQ },
                ]}
                className="max-sm:w-full"
              />
            )}
            <div className="relative min-w-0 flex-1 basis-56 sm:max-w-xs">
              <Search
                size={16}
                strokeWidth={1.75}
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
                aria-hidden
              />
              <input
                className={`${inputCls} pl-9`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Найти по ФИО или № СКУД"
                aria-label="Поиск по ФИО или № СКУД"
                autoComplete="off"
              />
            </div>
          </div>
          <TimesheetGrid
            ts={ts}
            selected={panel === 'day' ? day : null}
            onCellClick={(personId, _name, c) => onCellClick(personId, c.date)}
            onName={openPerson}
            visible={visible}
            questions={grid}
          />
        </div>
      )}

      <Legend questions={questionsOn} />

      <DayCard
        open={panel === 'day' && Boolean(selCell)}
        onClose={() => guard(closePanel)}
        timesheetId={id}
        timesheetName={ts.brigadeName}
        personId={day?.personId ?? ''}
        displayName={selRow?.displayName ?? ''}
        cell={selCell}
        dayOff={dayOffLabel(selDay)}
        tzOffset={me.object.tzOffset}
        actions={actions}
        hoursFormat={day ? format(day.personId, day.date) : null}
        onCopyLink={copyLink}
        onHowItWorks={() => guard(() => navigate(rulesHref({ date: day?.date, from: `/timesheets/${id}` })))}
        returnFocus={() => (day ? findCell(cellKey(day.personId, day.date)) : null)}
        questions={qdata ? allQs : undefined}
        focusQuestion={dayFrom.questionId}
        canAnswer={brigadierEdits}
        canReview={hrEdits}
        role={role}
        nav={nav}
        onBack={
          dayFrom.back === 'list'
            ? () => guard(() => setPanel('list'))
            : dayFrom.back === 'person'
              ? () => guard(() => setPanel('person'))
              : undefined
        }
        onAnswered={onAnswered}
        onReviewed={onReviewed}
        onStale={onStale}
        onComplete={brigadierEdits ? () => guard(() => setCompleting(true)) : undefined}
        undo={undo}
      />

      {person !== null && (
        <PersonCard
          open={panel === 'person'}
          onClose={() => guard(closePanel)}
          ts={ts}
          personId={person}
          questions={qdata ? allQs : undefined}
          canEdit={brigadierEdits}
          onOpenDay={(date, questionId) =>
            guard(() => showDay(person, date, { from: 'person', questionId: questionId ?? null }))
          }
          onAnswer={() => answerPerson(person)}
          onRemove={() => removePerson(person)}
        />
      )}

      {questionsOn && (
        <QuestionsList
          key={listPreset.n}
          open={panel === 'list'}
          onClose={() => guard(closePanel)}
          timesheetName={ts.brigadeName}
          questions={allQs}
          loading={qq.isLoading}
          error={qq.error}
          canAnswer={brigadierEdits}
          review={review}
          preset={listPreset.preset}
          tzOffset={me.object.tzOffset}
          format={anyFormat}
          onOpen={(x) => guard(() => showDay(x.personId, x.date, { from: 'list', questionId: x.id }))}
          onBulk={(ids) =>
            guard(() => {
              setBulk({ ids, fromList: true });
              setPanel('bulk');
            })
          }
          onQueue={startQueue}
        />
      )}

      {questionsOn && brigadierEdits && (
        <BulkAnswer
          open={panel === 'bulk'}
          onClose={() => guard(closePanel)}
          onBack={bulk.fromList ? () => guard(() => setPanel('list')) : undefined}
          timesheetId={id}
          timesheetName={ts.brigadeName}
          questions={bulkQs}
          format={anyFormat}
          onSaved={(res) => {
            onAnswered(res);
            setPanel(bulk.fromList ? 'list' : null);
          }}
          onStale={onStale}
          undo={undo}
        />
      )}

      <SidePanel
        open={panel === 'done'}
        onClose={closePanel}
        label="Вопросы по табелю"
        crumb={ts.brigadeName}
        title="Вопросы по табелю"
        icon={MessageCircleQuestion}
        footer={
          <>
            <span className="flex-1" aria-hidden />
            <Button onClick={closePanel}>Закрыть</Button>
            {brigadierEdits && (
              <Button variant="primary" icon={Send} onClick={() => setCompleting(true)}>
                Завершить табель
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col items-center px-4 py-10 text-center">
          <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-card bg-ok-soft text-ok">
            <CircleCheck size={22} strokeWidth={1.75} aria-hidden />
          </span>
          <p className="text-[15px] font-semibold text-ink">Все вопросы отвечены</p>
          <p className="mt-1 max-w-sm text-[13px] text-ink-2">
            Ответы стоят в ячейках табеля и в журнале действий. Отвечено за этот проход: {answeredInQueue}.
          </p>
        </div>
      </SidePanel>

      {brigadierEdits && (
        <PeoplePanel
          open={panel === 'people'}
          onClose={() => guard(closePanel)}
          ts={ts}
          tzOffset={me.object.tzOffset}
          busy={run.isPending}
          initialTab={peopleTab}
          onAdd={addPeople}
          onCopy={copyPeople}
        />
      )}

      {brigadierEdits && (
        <CompleteDialog
          open={completing}
          onClose={() => setCompleting(false)}
          timesheetId={id}
          ts={ts}
          onAnswerNow={() => {
            setCompleting(false);
            startQueue();
          }}
          onCompleted={() => {
            setCompleting(false);
            setPanel(null);
            showInfo('Табель завершён и передан кадрам');
            refresh();
          }}
        />
      )}

      <DeleteDraftDialog
        draft={
          deleting
            ? {
                id,
                brigadeName: ts.brigadeName,
                periodStart: ts.periodStart,
                periodEnd: ts.periodEnd,
                people: ts.rows.length,
                hours: shownHours(ts.grandTotalValue, ts.grandTotal) || '0',
                // Как в подтверждении из списка табелей (T117): все вопросы без снятых и сколько без ответа.
                questions: qdata ? allQs.length : undefined,
                openQuestions: qdata ? unanswered.length : undefined,
              }
            : null
        }
        onClose={() => setDeleting(false)}
        // Замена в истории: «Назад» не ведёт на удалённый табель.
        onDeleted={() => navigate(home, { replace: true })}
        screen={SCREEN}
      />

      <Modal
        open={returning}
        onClose={() => setReturning(false)}
        title="Вернуть табель бригадиру"
        description={
          clarify.length > 0
            ? 'Бригадир увидит табель в работе. Вопросы с «Уточнить» станут у него снова без ответа.'
            : 'Бригадир увидит табель в работе и сможет исправить часы.'
        }
      >
        {clarify.length > 0 && (
          <div className="mb-4">
            <p className="mb-1.5 text-[13px] font-medium text-ink">Уточнить: {nq(clarify.length)}</p>
            <ul aria-label="Вопросы «Уточнить»" className="flex flex-col gap-1 text-[13px] text-ink-2">
              {clarify.map((x) => (
                <li key={x.id} className="rounded-control border border-line px-3 py-2">
                  {`${x.displayName} · ${dayLabel(x.date)} · ${typeLabel(x.type)} — «${x.review?.comment ?? ''}»`}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Field label="Что исправить (обязательно)">
          <textarea
            className={`${textareaCls} h-24`}
            value={returnComment}
            maxLength={500}
            onChange={(e) => setReturnComment(e.target.value)}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={() => setReturning(false)}>Отмена</Button>
          <Button
            variant="primary"
            disabled={!returnComment.trim() || run.isPending}
            onClick={() =>
              run.mutate(async () => {
                await api.returnTs(id, returnComment.trim());
                setReturning(false);
                setReturnComment('');
                setPanel(null);
              })
            }
          >
            Вернуть
          </Button>
        </div>
      </Modal>

      <Modal
        open={signing}
        onClose={() => setSigning(false)}
        title="Подписать табель?"
        description="Подписанный табель нельзя изменить ни в одной роли."
      >
        <div className="flex justify-end gap-2">
          <Button onClick={() => setSigning(false)}>Отмена</Button>
          <Button
            variant="danger"
            disabled={run.isPending}
            onClick={() =>
              run.mutate(async () => {
                try {
                  await api.sign(id);
                } catch (e) {
                  // Пересчёт начался после открытия окна (FR-043): тот же текст в тосте, табель — перечитать.
                  if (e instanceof ApiError && e.code === 'RECALC_IN_PROGRESS') {
                    setSigning(false);
                    refresh();
                  }
                  throw e;
                }
                setSigning(false);
                showInfo('Табель подписан');
              })
            }
          >
            Подписать
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Legend({ questions }: { questions: boolean }) {
  const swatch = (cls: string, text: string) => (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1">
      <span className={`inline-block h-2.5 w-2.5 rounded-[3px] ring-1 ring-line-strong ${cls}`} aria-hidden />
      {text}
    </span>
  );
  const pill = (children: ReactNode) => (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1">
      {children}
    </span>
  );
  const bad = 'rounded-[3px] bg-bad-soft px-1 font-semibold text-bad';
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-2">
      {swatch('bg-weekend', 'выходной, праздник')}
      {swatch('bg-edit', 'изменено вручную ✎')}
      {swatch('bg-lunch', 'работал в обед')}
      {pill(
        <>
          <b className={bad}>н/вых</b> <b className={bad}>н/вх</b> нет выхода / входа
        </>,
      )}
      {pill(
        <>
          <b className={bad}>&gt;24 ч</b> за дату больше 24 ч — проверьте смены
        </>,
      )}
      {questions &&
        pill(
          <>
            <span className="relative inline-block h-3 w-3 rounded-[3px] ring-1 ring-line-strong" aria-hidden>
              <span className="absolute top-0 right-0 h-0 w-0 border-t-[6px] border-l-[6px] border-t-warn border-l-transparent" />
            </span>
            <span>вопрос без ответа</span>
          </>,
        )}
      {questions &&
        pill(
          <>
            <b className="font-bold text-ok" aria-hidden>
              ✓
            </b>
            <span>ответ бригадира</span>
          </>,
        )}
      {pill(
        <>
          <i className="text-ink-3">на об.</i> сейчас на объекте
        </>,
      )}
      {pill(
        <>
          <Users size={12} strokeWidth={2} className="text-warn" aria-hidden /> две бригады в один день
        </>,
      )}
      {pill('О, Б, ДО — отпуск, больничный, без содержания')}
      <span className="ml-auto inline-flex items-center gap-1.5 text-ink-3">
        <Info size={14} strokeWidth={1.75} aria-hidden />
        {questions
          ? 'Ячейка — карточка дня справа; ФИО — карточка сотрудника; дата с числом — вопросы дня'
          : 'Ячейка — карточка дня справа; ФИО — карточка сотрудника'}
      </span>
    </div>
  );
}

/** Подсказка о статусе: что можно делать с табелем в текущей роли. */
function StatusNote({ ts, role, tzOffset }: { ts: Timesheet; role: AppRole; tzOffset: string }) {
  if (ts.status === 'signed')
    return (
      <Callout tone="ok" icon={CircleCheck}>
        Подписан{ts.signedBy ? `: ${ts.signedBy}, ${ruDateTime(ts.signedAt, tzOffset)}` : ''}. Изменить
        нельзя.
      </Callout>
    );
  if (ts.status === 'returned' && role === 'brigadier')
    return (
      <Callout tone="warn" icon={Undo2}>
        Кадры вернули табель на доработку. {ts.returnComment ? `«${ts.returnComment}» ` : ''}Исправьте часы и
        нажмите «Завершить».
      </Callout>
    );
  if (ts.status === 'completed' && role === 'brigadier')
    return (
      <Callout tone="accent" icon={Send}>
        Табель передан кадрам. Пока он на проверке, изменения недоступны.
      </Callout>
    );
  if (ts.status === 'completed' && role === 'hr')
    return (
      <Callout tone="accent" icon={Info}>
        Проверьте часы. Исправить ячейку можно с комментарием, отсутствие — кодом О, Б или ДО.
      </Callout>
    );
  return null;
}
